import { useRef, useState } from 'react';
import {
    Flex,
    Text,
    Button,
    Box,
    Heading,
    useToast,
    Select,
    Icon,
    useDisclosure,
    chakra,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
} from '@chakra-ui/react';

import { useNavigate } from 'react-router';

import PageHeader from 'layout/pageHeader';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { useExportPDPsErrorsMutation, useLazyExportPDPsQuery, useUploadPDPsMutation } from 'core/API/pdpApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import MDropZone from 'components/molecules/MDropZone';
import { linkStyle } from 'shared/styles';
import ARetLangSelector from 'components/atoms/ARetLangSelector';
import { TRetailerProp } from 'shared/types';
import icons from 'shared/icons';
import { isFetchBaseQueryError } from 'utils/erros';
import { useTranslation } from 'helpers/languages/translations';
import FileSaver from 'file-saver';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import moment from 'moment';
import { WeeklyCalendar } from 'components/molecules/WeeklyCalendar';
import { TPDPExcelData } from 'types';
import { isPDPPermissionError } from 'pages/pdp/errors';

const PDPUpload = () => {
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [retailer, setRetailer] = useState<TRetailerProp>('default');
    const [lang, setLang] = useState<string>('default');
    const [uploadMethod, setUploadMethod] = useState<'keep' | 'overwrite'>('keep');

    const [errorList, setErrorList] = useState<TPDPExcelData[]>([]);

    const [exportPDPErrors] = useExportPDPsErrorsMutation();
    const [modalType, setModalType] = useState<'overwrite' | 'error'>('overwrite');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const overwriteInitialRef = useRef(null);
    const [dateInWeeks, setDateInWeeks] = useState<{ week?: number, year?: number }>({
        week: moment(new Date()).isoWeek(),
        year: new Date().getFullYear(),
    });
    const [faultyRowCount, setFaultyRowCount] = useState<number>(0);
    const navigate = useNavigate();
    const translate = useTranslation();
    const [exportPDP, { isLoading: isExportLoading }] = useLazyExportPDPsQuery();
    const toast = useToast();
    const [uploadPDPs, { isLoading }] = useUploadPDPsMutation();

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const goBack = () => navigate(-1);

    const getTemplate = async () => {
        try {
            const exportParams = {
                isTemplate: true,
            };

            const { data: res, isError } = await exportPDP(exportParams);
            if (isError || !res) throw new Error('PDP_template.xlsx');

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, 'PDP_template.xlsx');
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };


    const handleUploadPDPs = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('retailer', retailer === 'default' ? 'default' : `${retailer.id}`);
            bodyFormData.append('lang', lang);
            bodyFormData.append('uploadMethod', uploadMethod);
            bodyFormData.append('date', dateInWeeks.week && dateInWeeks.year ? `${String(dateInWeeks.week).padStart(2, '0')}_${dateInWeeks.year}` : '');

            const res = await uploadPDPs(bodyFormData);
            if ('error' in res) {
                const errorObj = res.error;
                if (isPDPPermissionError(errorObj)) {
                    throw new Error(translate('pdp.error.permission_denied') as string);
                }
                if (isFetchBaseQueryError(errorObj)) {
                    const errorType = (errorObj.data as any).type as string | undefined;
                    if (errorType === 'empty_file') {
                        throw new Error(translate('product.upload.error.empty_file') as string);
                    }
                    if (errorType === 'missing_headers') {
                        const missingHeaders = (errorObj.data as any).missing_headers as string[] | undefined;
                        if (missingHeaders && missingHeaders.length) {
                            const missingHeadersStr = missingHeaders.map((h) => `« ${h} »`).join(', ');
                            throw new Error(translate('product.upload.error.invalidHeaders', { headers: missingHeadersStr }) as string);
                        }
                    }

                    if (errorType === 'invalid_data') {
                        const data = (errorObj.data as any).data as TPDPExcelData[] | undefined;
                        if (data) setErrorList(data);
                        setFaultyRowCount((errorObj.data as any).faulty_row_count || 0);
                        setModalType('error');
                        onOpen();
                        return;
                    }
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('pdp.upload.error') as string);
            }

            setUploadedFile(null);
            goBack();
            toast({
                title: translate('pdp.upload.success'),
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;
        const handleChangeUploadMethod = (e: React.ChangeEvent<HTMLSelectElement>) => {
            setUploadMethod(e.target.value as 'keep' | 'overwrite');
        };


        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start" mr={5}>
                <Text mb={5}>
                    {translate('product.upload.recommendation', {
                        template: (
                            <Text onClick={getTemplate} as="span" {...linkStyle}>
                                {translate('product.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>

                <Text>{translate('pdp.upload.retailerSelect')}</Text>
                <ARetLangSelector
                    selectedRetailer={retailer}
                    selectedLang={lang}
                    setSelectedLang={setLang}
                    setSelectedRetailer={setRetailer}
                    selectProps={{ size: 'md', mt: 3, mb: 3 }}
                    omitLang={true}
                />

                <Text mt={2} mb={2}>{translate('pdp.upload.week')}</Text>

                <WeeklyCalendar dateInWeeks={dateInWeeks} setDateInWeeks={setDateInWeeks} style={{ alignItems: "flex-start", width: "100%", fontSize: "16px", height: "37px", marginBottom: "15px" }} textStyle={{ justifyContent: "flex-start" }} />

                <Text>{translate('product.upload.method')}</Text>
                <Select
                    borderRadius={6}
                    size="md"
                    mb={8}
                    mt={2}
                    bg="white"
                    value={uploadMethod}
                    onChange={handleChangeUploadMethod}
                >
                    <option value="keep">{translate('product.upload.method.keep')}</option>
                    <option value="overwrite">{translate('product.upload.method.override')}</option>
                </Select>

                <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={
                            uploadMethod === 'overwrite'
                                ? onOpen : handleUploadPDPs
                        }
                        isLoading={isLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('product.upload.apply')}
                    </Button>
                </Flex>
            </Flex>
        );
    };


    const handleDownloadErrorFile = async () => {
        try {
            const res = await exportPDPErrors(errorList);
            if ('error' in res) throw new Error(translate('error.export') as string);

            const blob = new Blob([res.data], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('pdp.export.error_file') as string);
        } catch (error) {
            toast({
                title: (error as Error).message || translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };


    const renderModal = () => {
        const overwirteTitle =
            uploadMethod === 'overwrite'
                ? translate('pdp.upload.modal.overwriteHeader')
                : translate('pdp.upload.modal.error.title');
        const title =
            modalType === 'overwrite' ? overwirteTitle : translate('pdp.upload.modal.error.title');
        const owModalContent =
            uploadMethod === 'overwrite' ? (
                <>
                    <Text>{translate('pdp.upload.modal.overwriteDesc')}</Text>
                    <Text>{translate('pdp.upload.modal.overwriteDesc2')}</Text>
                </>
            ) : (
                <Text>{translate('pdp.upload.modal.overwriteDesc')}</Text>
            );

        const submitButton =
            modalType === 'overwrite' ? (
                <Button
                    onClick={handleUploadPDPs}
                    isLoading={isLoading}
                    colorScheme="red"
                    size="sm"
                >
                    {uploadMethod === 'overwrite'
                        ? translate('pdp.upload.modal.overwrite')
                        : translate('pdp.upload.modal.apply')}
                </Button>
            ) : (
                <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                    {translate('pdp.upload.modal.cancel')}
                </Button>
            );

        const bodyContent =
            modalType === 'overwrite' ? (
                owModalContent
            ) : (
                <Text>
                    {translate('page.pdp.errorsUploadedDescription', {
                        nb: faultyRowCount,
                        downloadComp: (
                            <chakra.span
                                onClick={intentionalyFloatingPromiseReturn(handleDownloadErrorFile)}
                                {...linkStyle}
                            >
                                {translate('page.pdp.errorDownload')}
                            </chakra.span>
                        ),
                    })}
                </Text>
            );

        return (
            <Modal isCentered initialFocusRef={overwriteInitialRef} isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1} ref={overwriteInitialRef}>
                        <Text as="h3" color={modalType === 'error' ? 'red.600' : 'gray.900'}>
                            {title}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isLoading} />
                    <ModalBody fontSize="sm">{bodyContent}</ModalBody>

                    <ModalFooter>
                        {modalType === 'overwrite' && (
                            <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                                {translate('pdp.upload.modal.cancel')}
                            </Button>
                        )}
                        {submitButton}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(27);
    // if (readonly) {
    //     window.location.href = `/${pdp.PATH}`;
    //     return null;
    // }

    return (
        <Box>
            <PageHeader
                pageTitle={translate('pdp.upload.headerInfo') as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex>
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb={5}>
                        {translate('product.upload.uploadFile')}
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    {renderModal()}
                </Flex>
            </Box>
        </Box>
    );
};

export default PDPUpload;
